import * as am5 from '@amcharts/amcharts5';
import {
  createCategoryAxis,
  createChartRoot,
  createColumnSeriesCollection,
  createDateAxis,
  createLineSeriesCollection,
  createToggleLegend,
  createValueAxis,
  createXYChart,
  createXYCursor,
  SharedColumnSeriesDefinition,
  SharedLineSeriesDefinition,
} from '../../Charts/amcharts-xy';
import { ModeAffichageGraphique } from '../../Services/installation';

export type ChartPoint = {
  date: number;
  production: number;
  ensoleillement: number;
};

type ColumnPoint = ChartPoint & { dateStr: string };

/** Libellé et unité d'une grandeur affichée sur le graphique. */
export type ChampInfo = {
  libelle?: string;
  unite?: string;
};

type ChartOptions = {
  data?: ChartPoint[];
  production?: ChampInfo;
  ensoleillement?: ChampInfo;
  mode?: ModeAffichageGraphique;
  /** Si `false`, les noms de séries n'incluent pas le code métier entre parenthèses. Défaut : `true`. */
  afficherCodeDansTitre?: boolean;
};

/** Formate une valeur issue directement du contexte de données amCharts. */
function valeurAvecUnite(champ: keyof ChartPoint, unite: string | undefined): string {
  const format = unite ? `#,###.##' ${unite}'` : `#,###.##`;
  return `{${champ}.formatNumber("${format}")}`;
}

/**
 * Construit le nom d'une série de graphique.
 *
 * @param label         Nom de la grandeur (ex. "Production", "Ensoleillement").
 * @param code          Identifiant du point de mesure
 * @param afficherCode  Indique si le code doit apparaître entre parenthèses.
 *
 * @example
 * nomSerie('Production', 'MR12', true)       // → 'Production (MR12)'
 * nomSerie('Production', 'MR12', false)      // → 'Production'
 * nomSerie('Production', undefined, true)    // → 'Production (indisponible)'
 */
function nomSerie(label: string, code: string | undefined, afficherCode: boolean): string {
  if (!afficherCode)
    return label;
  
  return `${label} (${code ?? 'indisponible'})`;
}

export function createChart(host: HTMLDivElement, options: ChartOptions = {}): am5.Root {
  const mode = options.mode ?? 'jour';
  return mode !== 'jour' ? createColumnChart(host, options, mode) : createLineChart(host, options);
}

function createLineChart(host: HTMLDivElement, options: ChartOptions): am5.Root {
  const root = createChartRoot(host);
  const chartData = options.data ?? [];
  const afficherCode = options.afficherCodeDansTitre !== false;
  const chart = createXYChart(root, {
    enableHorizontalZoom: true,
    enableHorizontalPan: false,
    showAllSeriesTooltips: true,
    paddingBottom: 8,
  });
  // L'API fournit un point toutes les 10 minutes. Le baseInterval doit refléter
  // cette granularité pour que DateAxis indexe et accroche chaque point.
  const xAxis = createDateAxis(root, chart, {
    baseInterval: { timeUnit: 'minute', count: 10 },
  });
  const yAxisLeft = createValueAxis(root, chart, { min: 0, strictMinMax: true, unite: options.production?.unite });
  const yAxisRight = createValueAxis(root, chart, { opposite: true, min: 0, strictMinMax: true, unite: options.ensoleillement?.unite });
  const tooltipPartage = [
    `{valueX.formatDate('dd/MM/yyyy HH:mm')}`,
    `Production : ${valeurAvecUnite('production', options.production?.unite)}`,
    `Ensoleillement : ${valeurAvecUnite('ensoleillement', options.ensoleillement?.unite)}`,
  ].join('\n');
  const seriesDefinitions: SharedLineSeriesDefinition<ChartPoint>[] = [
    {
      name: nomSerie('Production', options.production?.libelle, afficherCode),
      valueYField: 'production',
      color: 0x327aff,
      yAxis: yAxisLeft,
      fillOpacity: 0.3,
      tooltipText: tooltipPartage,
    },
    {
      name: nomSerie('Ensoleillement', options.ensoleillement?.libelle, afficherCode),
      valueYField: 'ensoleillement',
      color: 0xffcc33,
      yAxis: yAxisRight,
      fillOpacity: 0.7,
      showTooltip: false,
    },
  ];
  chart.set('wheelX', 'none');
  const series = createLineSeriesCollection(root, chart, xAxis, chartData, seriesDefinitions);
  // Garder l’ensoleillement derrière la production sans changer l’ordre de la légende.
  series[1].toBack();
  createXYCursor(root, chart, {
    // Le premier tooltip contient déjà les valeurs des deux séries.
    snapToSeries: [series[0]],
    snapToSeriesBy: 'x',
    behavior: 'selectX',
    showHorizontalLine: false,
  });
  createToggleLegend(root, chart, series);
  chart.appear(600, 100);
  return root;
}

function createColumnChart(host: HTMLDivElement, options: ChartOptions, mode: 'mois' | 'annee'): am5.Root {
  const root = createChartRoot(host);
  const afficherCode = options.afficherCodeDansTitre !== false;
  const chartData: ColumnPoint[] = (options.data ?? []).map(p => {
    const d = new Date(p.date);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { ...p, dateStr };
  });

  const chart = createXYChart(root, { paddingBottom: 8, paddingLeft: 6, paddingRight: 6 });
  const xAxis = createCategoryAxis(root, chart, chartData, {
    categoryField: 'dateStr',
    minGridDistance: mode === 'annee' ? 40 : 55,
  });

  xAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
    const category = target.dataItem?.get('category' as never) as string | undefined;
    if (!category) return _text;
    const d = new Date(category);
    if (isNaN(d.getTime())) return _text;
    return mode === 'annee'
      ? d.toLocaleDateString('fr-FR', { month: 'short' })
      : String(d.getUTCDate());
  });

  const yAxisLeft = createValueAxis(root, chart, { min: 0, strictMinMax: true, unite: options.production?.unite });
  const yAxisRight = createValueAxis(root, chart, { opposite: true, min: 0, strictMinMax: true, unite: options.ensoleillement?.unite });
  const tooltipPartage = [
    `{categoryX}`,
    `Production : ${valeurAvecUnite('production', options.production?.unite)}`,
    `Ensoleillement : ${valeurAvecUnite('ensoleillement', options.ensoleillement?.unite)}`,
  ].join('\n');

  const seriesDefinitions: SharedColumnSeriesDefinition<ColumnPoint>[] = [
    {
      name: nomSerie('Production', options.production?.libelle, afficherCode),
      categoryXField: 'dateStr',
      valueYField: 'production',
      color: 0x327aff,
      yAxis: yAxisLeft,
      tooltipText: tooltipPartage,
      widthPercent: 45,
    },
    {
      name: nomSerie('Ensoleillement', options.ensoleillement?.libelle, afficherCode),
      categoryXField: 'dateStr',
      valueYField: 'ensoleillement',
      color: 0xffcc33,
      yAxis: yAxisRight,
      showTooltip: false,
      widthPercent: 45,
    },
  ];

  const series = createColumnSeriesCollection(root, chart, xAxis, chartData, seriesDefinitions);
  // Même ordre de superposition pour les vues mensuelle et annuelle.
  series[1].toBack();
  // Le curseur permet d'accrocher la catégorie la plus proche sans devoir viser
  // exactement une colonne. Le tooltip partagé contient les deux grandeurs.
  createXYCursor(root, chart, {
    snapToSeries: [series[0]],
    snapToSeriesBy: 'x',
    showHorizontalLine: false,
  });
  createToggleLegend(root, chart, series);
  chart.appear(600, 100);
  return root;
}
